"use strict";

/**
 * @classdesc
 * Layergruppe
 *
 * @class
 * @extends gc.gis.layer.GCAbstractLayer
 * @memberof gc.gis.layer
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @param {Object} options Optionen (siehe auch Optionen in {@link gc.gis.layer.GCAbstractLayer})
 * @see {@link gc.gis.layer.GCAbstractLayer}
 */
gc.gis.layer.GCGroupLayer = function(options)
{
	var node = new gc.gis.util.GCTreeNode({
		mutable: true,
		payload: this
	});

	gc.gis.layer.GCAbstractLayer.call(this, node, options);

	if(!options.originalType)
	{
		this._setConfig('type', gc.gis.layer.GCLayerType.GROUP);
	}
};
gc.gis.layer.GCGroupLayer.prototype = Object.create(gc.gis.layer.GCAbstractLayer.prototype);

/**
 * Gibt eine Objektrepräsentation dieser Layergruppe zurück. Unterebenen werden
 * rekursiv abgefragt.
 *
 * @public
 * @method
 * @return {Object} Objektrepräsentation des Layers
 */
gc.gis.layer.GCGroupLayer.prototype.toObject = function()
{
	var config = gc.gis.layer.GCAbstractLayer.prototype.toObject.call(this);

	// Items aktualisieren
	config.items = [];
	var layers = this.getLayers();
	for( var i=0; i<layers.length; i++ )
	{
		config.items.push(layers[i].toObject());
	}

	return config;
};

/**
 * @override
 * @inheritdoc
 */
gc.gis.layer.GCGroupLayer.prototype.getExtent = function(callback, opt_crs)
{
	var self = this;

	var layers = this.getLayers();
	var extents = [];

	for( var i=0; i<layers.length; i++ )
	{
		layers[i].getExtent(function(result) {
			extents.push(result.extent);
			if(extents.length === layers.length)
			{
				callback.call(self, {
					layer: self,
					extent: gc.gis.GCUtil.mergeExtents(extents)
				});
			}
		}, opt_crs);
	}
};

/**
 * @override
 * @inheritDoc
 */
gc.gis.layer.GCGroupLayer.prototype.isQueryable = function()
{
    var layers = this.getLayers();

    for (var i=0; i<layers.length; i++)
    {
        // ein Gruppenlayer ist abfragbar, wenn ein Child abfragbar ist
        if (layers[i].isQueryable())
        {
            return true;
        }
    }

    return false;
};

/**
 * Löst diese Gruppe auf: Unterebenen dieses Gruppenlayers werden dem
 * übergeordneten Gruppenlayer am Index dieser Gruppe angefügt und diese
 * Gruppe aus der übergeordneten Gruppe herausgelöst.
 * <strong>Hinweis:</strong> Die Instanz dieses Layers bleibt erhalten und muss
 * ggf. extra behandelt werden.
 *
 * @public
 * @method
 * @throws {gc.gis.error.GCError} Wurzel-Layer können nicht aufgelöst werden
 */
gc.gis.layer.GCGroupLayer.prototype.dissolve = function()
{
	if(this._getNode().isRoot())
	{
		throw new gc.gis.error.GCError('Cannot dissolve root layer');
	}
	var parent_node = this._getNode().getParent();
	var index       = parent_node.indexOf(this._getNode());
	var children    = gc.gis.GCUtil.copyArray(this._getNode().getChildren());
	var layers		= [];
	for(var i=0; i<children.length; i++)
	{
		parent_node.insertAt(children[i], index + i, false);
		layers.push(children[i].getPayload());
	}

	if (layers.length > 0)
	{
        this._fireEvent(gc.gis.map.GCMapEvent.LAYER_MOVED, {
            source_layers: layers.reverse(),
            target_layer: parent_node.getPayload(),
            index: index
        });
    }

	this.getParent().removeLayer(this);
};

/**
 * Gibt die Unterebene am gegebenen Index zurück.
 *
 * @public
 * @method
 * @param {number} index  Layerindex
 * @return {gc.gis.layer.GCAbstractLayer} Unterlayer am Index
 * @throws {gc.gis.error.GCError} Index muss gültig sein
 */
gc.gis.layer.GCGroupLayer.prototype.getLayer = function(index)
{
	return this._getNode().getChildAt(index).getPayload();
};

/**
 * Gibt alle bzw. alle dem Filter entsprechenden Unterlayer dieser Gruppe zurück.
 *
 * @public
 * @method
 * @param {gc.gis.layer.GCLayerFilterCallback} [opt_filter_func]  Optionale Filterfunktion
 * @return {gc.gis.layer.GCAbstractLayer[]} Unterlayer-Liste
 *
 * @see gc.gis.util.GCTreeNode#getChildrenPayload
 */
gc.gis.layer.GCGroupLayer.prototype.getLayers = function(opt_filter_func)
{
	return this._getNode().getChildrenPayload(opt_filter_func);
};

/**
 * Sammelt rekursiv alle bzw. alle dem Filter entsprechenden Unterlayer dieser
 * Gruppe und gibt sie als <em>flache Liste</em> zurück.
 *
 * @public
 * @method
 * @param {gc.gis.layer.GCLayerFilterCallback} [opt_filter_func]  Optionale Filterfunktion
 * @return {gc.gis.layer.GCAbstractLayer[]} Unterlayer-Liste
 */
gc.gis.layer.GCGroupLayer.prototype.getLayersRecursive = function(opt_filter_func)
{
	var out = [];
	var layers = this.getLayers();

	var do_filter = (typeof opt_filter_func === 'function');

	for( var i=0; i<layers.length; i++ )
	{
		// Filter gesetzt und match?
		if( do_filter )
		{
			if(opt_filter_func.call(this, layers[i]))
			{
				out.push(layers[i]);
			}
		}
		else
		{
			out.push(layers[i]);
		}
		// Untergruppen durchsuchen
		if(layers[i] instanceof gc.gis.layer.GCGroupLayer)
		{
			out = out.concat(layers[i].getLayersRecursive(opt_filter_func));
		}
	}
	return out;
};

/**
 * Sucht im eigenen Baum nach dem Layer mit dem gegebenen Index.
 *
 * @public
 * @method
 * @param {number[]} index gesuchter Index
 * @return {gc.gis.layer.GCAbstractLayer|null} Layerinstanz oder <code>null</code>, falls
 *   nicht gefunden
 */
gc.gis.layer.GCGroupLayer.prototype.findLayer = function(index)
{
	var node = this._getNode().findNode(index);
	return node ? node.getPayload() : null;
};

/**
 * Fügt einen Layer hinzu.
 *
 * @public
 * @method
 * @param {gc.gis.layer.GCAbstractLayer} layer hinzuzufügender Layer
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @fires gc.gis.map.GCMapEvent#layer:add
 */
gc.gis.layer.GCGroupLayer.prototype.addLayer = function(layer, opt_fire_event)
{
    this._getNode().addChild(layer._getNode(), opt_fire_event);

    // Event wird bereits duch Parent-Class "gc.gis.layer.GCAbstractLayer" gefeuert siehe Methode "_initEvents"
    // und zudem im Baum nach oben gebubbled
};

/**
 * Fügt einen Layer an einem Index hinzu.
 *
 * @public
 * @method
 * @param {gc.gis.layer.GCAbstractLayer} layer hinzuzufügender Layer
 * @param {number} index  Index (Integer)
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @fires gc.gis.map.GCMapEvent#layer:add
 */
gc.gis.layer.GCGroupLayer.prototype.insertLayerAt = function(layer, index, opt_fire_event)
{
    this._getNode().insertAt(layer._getNode(), index, opt_fire_event);

    // Event wird bereits duch Parent-Class "gc.gis.layer.GCAbstractLayer" gefeuert siehe Methode "_initEvents"
    // und zudem im Baum nach oben gebubbled
};

/**
 * Fügt mehrere Layer hinzu.
 *
 * @public
 * @method
 * @param {gc.gis.layer.GCAbstractLayer[]} layers Layer
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @fires gc.gis.map.GCMapEvent#layer:add
 */
gc.gis.layer.GCGroupLayer.prototype.addLayers = function(layers, opt_fire_event)
{
	var nodes = [];
	for(var i=0; i<layers.length; i++)
	{
		nodes.push(layers[i]._getNode(), opt_fire_event);
	}
	this._getNode().addChildren(nodes);

    // Event wird bereits duch Parent-Class "gc.gis.layer.GCAbstractLayer" gefeuert siehe Methode "_initEvents"
    // und zudem im Baum nach oben gebubbled
};

/**
 * Entfernt eine Layerinstanz.
 *
 * @public
 * @method
 * @param {gc.gis.layer.GCAbstractLayer} layer zu entfernender Layer
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @fires gc.gis.map.GCMapEvent#layer:remove
 */
gc.gis.layer.GCGroupLayer.prototype.removeLayer = function(layer, opt_fire_event)
{
	this._getNode().removeChild(layer._getNode(), opt_fire_event);

    // Event wird bereits duch Parent-Class "gc.gis.layer.GCAbstractLayer" gefeuert siehe Methode "_initEvents"
	// und zudem im Baum nach oben gebubbled
};

/**
 * Entfernt den Layer am Index.
 *
 * @public
 * @method
 * @param {number} index zu entfernender Layer
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @throws {gc.gis.error.GCError} Index muss gültig sein
 * @fires gc.gis.map.GCMapEvent#layer:remove
 */
gc.gis.layer.GCGroupLayer.prototype.removeLayerAt = function(index, opt_fire_event)
{
	this._getNode().removeChildAt(index, opt_fire_event);

    // Event wird bereits duch Parent-Class "gc.gis.layer.GCAbstractLayer" gefeuert siehe Methode "_initEvents"
    // und zudem im Baum nach oben gebubbled
};

/**
 * Entfernt alle Layer dieser Gruppe.
 *
 * @public
 * @method
 * @param {boolean} [opt_fire_event=true]  Flag, ob Event abgefeuert werden soll
 * @fires gc.gis.map.GCMapEvent#layer:remove
 */
gc.gis.layer.GCGroupLayer.prototype.removeAllLayers = function(opt_fire_event)
{
	this._getNode().removeAll(opt_fire_event);

    // Event wird bereits duch Parent-Class "gc.gis.layer.GCAbstractLayer" gefeuert siehe Methode "_initEvents"
    // und zudem im Baum nach oben gebubbled
};
